/** Shared MJPEG multipart helpers for live camera preview. */

export const abortMjpegImage = (ref) => {
  const img = ref?.current;
  if (!img) return;
  img.onload = null;
  img.onerror = null;
  img.src = 'about:blank';
  img.removeAttribute('src');
};

export const abortMjpegSession = (abortRef, imgRef) => {
  try {
    abortRef?.current?.abort();
  } catch (_) {
    /* ignore */
  }
  if (abortRef) abortRef.current = null;
  abortMjpegImage(imgRef);
};

const MAX_MJPEG_BUFFER = 4 * 1024 * 1024;

const concatBytes = (left, right) => {
  const out = new Uint8Array(left.length + right.length);
  out.set(left, 0);
  out.set(right, left.length);
  return out;
};

const extractNextJpeg = (buffer) => {
  let soi = -1;
  for (let i = 0; i < buffer.length - 1; i += 1) {
    if (buffer[i] === 0xff && buffer[i + 1] === 0xd8) {
      soi = i;
      break;
    }
  }
  if (soi < 0) {
    return { jpeg: null, rest: buffer.length > MAX_MJPEG_BUFFER ? buffer.slice(-2) : buffer };
  }
  for (let i = soi + 2; i < buffer.length - 1; i += 1) {
    if (buffer[i] === 0xff && buffer[i + 1] === 0xd9) {
      return { jpeg: buffer.subarray(soi, i + 2), rest: buffer.subarray(i + 2) };
    }
  }
  const rest = soi > 0 ? buffer.subarray(soi) : buffer;
  if (rest.length > MAX_MJPEG_BUFFER) {
    return { jpeg: null, rest: rest.slice(-2) };
  }
  return { jpeg: null, rest };
};

const toUint8 = (bytes) => (bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes));

/** Fetch multipart MJPEG so AbortController can close the HTTP connection immediately. */
export const pumpMjpegStream = async ({ url, imgRef, signal, onFrame, onError, onEnded }) => {
  let blobUrl = null;
  try {
    const response = await fetch(url, {
      signal,
      credentials: 'include',
      cache: 'no-store',
      headers: { Accept: 'multipart/x-mixed-replace' },
    });
    if (!response.ok || !response.body) {
      throw new Error(`mjpeg http ${response.status}`);
    }
    const reader = response.body.getReader();
    let buffer = new Uint8Array(0);
    try {
      while (!signal.aborted) {
        const { done, value } = await reader.read();
        if (value && value.length) {
          buffer = concatBytes(buffer, value);
        }
        let extracted = extractNextJpeg(buffer);
        buffer = toUint8(extracted.rest);
        while (extracted.jpeg) {
          const jpegCopy = extracted.jpeg.slice();
          const nextUrl = URL.createObjectURL(new Blob([jpegCopy], { type: 'image/jpeg' }));
          const previousUrl = blobUrl;
          blobUrl = nextUrl;
          const img = imgRef?.current;
          // A multipart MJPEG <img src> never fires load/error inside a CSS
          // transform. A single JPEG blob does, so paint each frame that way.
          if (img) img.src = nextUrl;
          if (previousUrl) URL.revokeObjectURL(previousUrl);
          onFrame?.();
          extracted = extractNextJpeg(buffer);
          buffer = toUint8(extracted.rest);
        }
        if (done) break;
      }
    } finally {
      try {
        await reader.cancel();
      } catch (_) {
        /* ignore */
      }
    }
    if (!signal.aborted) onEnded?.();
  } catch (err) {
    if (signal.aborted || err?.name === 'AbortError') return;
    onError?.(err);
  } finally {
    // Keep the last frame on a clean end so the dialog does not flash blank
    // before the next reconnect. Abort (dialog close / URL change) releases it.
    if (signal.aborted && blobUrl) URL.revokeObjectURL(blobUrl);
  }
};

export const getOrderedCameraStreams = (camera) => {
  if (!camera) return [];
  if (Array.isArray(camera.streams) && camera.streams.length) {
    return camera.streams
      .map((s) => ({ streamId: s.id || null, streamUrl: (s.stream_url || '').trim() }))
      .filter((s) => s.streamUrl);
  }
  const urls =
    Array.isArray(camera.stream_urls) && camera.stream_urls.length
      ? camera.stream_urls.filter(Boolean)
      : camera.rtsp_url
        ? [camera.rtsp_url]
        : [];
  return urls
    .map((url) => ({ streamId: null, streamUrl: String(url).trim() }))
    .filter((s) => s.streamUrl);
};

/** Prefer assigned rtsp_url; else medium (2nd) stream when multiple exist. */
export const resolvePreviewStream = (camera) => {
  const streams = getOrderedCameraStreams(camera);
  if (!streams.length) return null;
  const preferred = (camera?.rtsp_url || '').trim();
  if (preferred) {
    const match = streams.find((s) => s.streamUrl === preferred);
    if (match) return match;
  }
  return streams[Math.min(1, streams.length - 1)];
};

export const withMjpegCacheBust = (url) => {
  if (!url) return url;
  const base = String(url).replace(/([?&])_r=\d+/g, '$1').replace(/[?&]$/, '');
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}_r=${Date.now()}`;
};
